import React, { useEffect, useState } from 'react';
import QRCode from 'qrcode';

type Language = 'en' | 'es';

interface Translations {
  legals: string;
  aboutUs: string;
  shareLabel: string;
  shareTooltip: string;
  qrTooltip: string;
  qrModalTitle: string;
  qrModalClose: string;
  copied: string;
}

interface TranslationsMap {
  en: Translations;
  es: Translations;
}

const translations: TranslationsMap = {
  en: {
    legals: 'Privacy Notice',
    aboutUs: 'About the survey',
    shareLabel: 'Share this survey',
    shareTooltip: 'Share',
    qrTooltip: 'Share with QR code',
    qrModalTitle: 'Scan to Share',
    qrModalClose: 'Close QR popup',
    copied: 'Link copied!',
  },
  es: {
    legals: 'Aviso de Privacidad',
    aboutUs: 'Sobre la encuesta',
    shareLabel: 'Comparte esta encuesta',
    shareTooltip: 'Compartir',
    qrTooltip: 'Compartir con codigo QR',
    qrModalTitle: 'Escanea para compartir',
    qrModalClose: 'Cerrar ventana QR',
    copied: 'Enlace copiado',
  },
};

const SHARE_TITLE = 'Check out this survey about environment management in Drupal projects';
const QR_SHARE_URL = 'https://survey.andor.cloud';

interface FooterComponentProps {
  language: Language;
  onLegalsClick: () => void;
  onAboutUsClick: () => void;
}

function FooterComponent({ language, onLegalsClick, onAboutUsClick }: FooterComponentProps): React.ReactElement {
  const t = translations[language];
  const currentUrl = window.location.href;
  const shareUrl = encodeURIComponent(currentUrl);
  const shareTitle = encodeURIComponent(SHARE_TITLE);
  const socialLinks = {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`,
    x: `https://twitter.com/intent/tweet?url=${shareUrl}&text=${shareTitle}`,
    instagram: 'https://www.instagram.com/',
    tiktok: 'https://www.tiktok.com/@ubiqus',
  };
  const [copied, setCopied] = useState(false);
  const [isQrModalOpen, setIsQrModalOpen] = useState(false);
  const [qrCodeSrc, setQrCodeSrc] = useState('');

  useEffect(() => {
    let isMounted = true;

    const generateQrCode = async (): Promise<void> => {
      try {
        const dataUrl = await QRCode.toDataURL(QR_SHARE_URL, {
          width: 420,
          margin: 1,
          color: {
            dark: '#0f1020',
            light: '#ffffff',
          },
        });

        if (isMounted) {
          setQrCodeSrc(dataUrl);
        }
      } catch {
        if (isMounted) {
          setQrCodeSrc('');
        }
      }
    };

    void generateQrCode();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!isQrModalOpen) {
      return undefined;
    }

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setIsQrModalOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isQrModalOpen]);

  const handleCopyLink = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(currentUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const textArea = document.createElement('textarea');
      textArea.value = currentUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const openShareWindow = (url: string): void => {
    window.open(url, '_blank', 'width=600,height=400,noopener,noreferrer');
  };

  return (
    <footer className="survey-footer">
      <div className="survey-footer__nav">
        <button
          className="survey-footer__link"
          onClick={onAboutUsClick}
        >
          {t.aboutUs}
        </button>
        <span className="survey-footer__separator">|</span>
        <button
          className="survey-footer__link"
          onClick={onLegalsClick}
        >
          {t.legals}
        </button>
      </div>

      <div className="survey-footer__share">
        <span className="survey-footer__share-label">{t.shareLabel}:</span>
        <div className="survey-footer__social-buttons">
          <button
            className="survey-footer__social-btn survey-footer__social-btn--facebook"
            onClick={() => openShareWindow(socialLinks.facebook)}
            title="Share on Facebook"
            aria-label="Share on Facebook"
          >
            <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
            </svg>
          </button>

          <button
            className="survey-footer__social-btn survey-footer__social-btn--x"
            onClick={() => openShareWindow(socialLinks.x)}
            title="Share on X"
            aria-label="Share on X"
          >
            <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
            </svg>
          </button>

          <button
            className="survey-footer__social-btn survey-footer__social-btn--instagram"
            onClick={() => openShareWindow(socialLinks.instagram)}
            title="Share on Instagram"
            aria-label="Share on Instagram"
          >
            <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
              <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
            </svg>
          </button>

          <button
            className="survey-footer__social-btn survey-footer__social-btn--tiktok"
            onClick={() => openShareWindow(socialLinks.tiktok)}
            title="Share on TikTok"
            aria-label="Share on TikTok"
          >
            <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
              <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
            </svg>
          </button>

          <button
            className="survey-footer__social-btn survey-footer__social-btn--qr"
            onClick={() => setIsQrModalOpen(true)}
            title={t.qrTooltip}
            aria-label={t.qrTooltip}
          >
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 4h6v6H4z" />
              <path d="M14 4h6v6h-6z" />
              <path d="M4 14h6v6H4z" />
              <path d="M15 15h1" />
              <path d="M19 15h1" />
              <path d="M15 19h1" />
              <path d="M17 17h1" />
              <path d="M19 19h1" />
              <path d="M17 13h1" />
            </svg>
          </button>

          <button
            className="survey-footer__social-btn survey-footer__social-btn--share"
            onClick={handleCopyLink}
            title={t.shareTooltip}
            aria-label={t.shareTooltip}
          >
            {copied ? (
              <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92-1.31-2.92-2.92-2.92z"/>
              </svg>
            )}
            <span className="survey-footer__copy-text">{copied ? t.copied : ''}</span>
          </button>
        </div>
      </div>

      {isQrModalOpen ? (
        <div
          className="survey-footer__qr-modal-overlay"
          onClick={() => setIsQrModalOpen(false)}
          role="presentation"
        >
          <div
            className="survey-footer__qr-modal"
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label={t.qrModalTitle}
          >
            <button
              className="survey-footer__qr-close"
              onClick={() => setIsQrModalOpen(false)}
              aria-label={t.qrModalClose}
            >
              <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <path d="M6 6l12 12" />
                <path d="M18 6L6 18" />
              </svg>
            </button>

            <h3 className="survey-footer__qr-title">{t.qrModalTitle}</h3>

            <div className="survey-footer__qr-frame">
              {qrCodeSrc ? (
                <img
                  className="survey-footer__qr-image"
                  src={qrCodeSrc}
                  alt={t.qrModalTitle}
                />
              ) : (
                <div className="survey-footer__qr-loading" aria-live="polite">
                  Loading...
                </div>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </footer>
  );
}

export default FooterComponent;
